import { WorkflowStepType } from '@commenda/types';
import { useMemo } from 'react';
import { ServiceAuditorStepHelper } from '../../../workflow/helpers/ServiceAuditorStepHelper';
import { getServiceRequestComponentMap } from '../../componentMap';
import { WorkflowServiceAuditTask } from '../../service-audit/components/WorkflowServiceAuditTask';
import { WorkflowServiceAuditWaitTask } from '../../service-audit/components/WorkflowServiceAuditWaitTask';
import { TCommonTaskProp } from '../../types';
import { SericeRequestUnknownTask } from '../../utils';
import MarkAsCompleteTask from './MarkAsCompleteTask';
import { DemoAIFillingAgentTask } from './workflow/DemoAIFillingAgentTask';
import { WorkflowDefaultTask } from './workflow/WorkflowDefaultTask';
import { WorkflowEsignTask } from './workflow/WorkflowEsignTask';
import { WorkflowHumanConfirmationTask } from './workflow/WorkflowHumanConfirmationTask';
import { WorkflowHumanFormTask } from './workflow/WorkflowHumanFormTask';
import { WorkflowHumanReviewTask } from './workflow/WorkflowHumanReviewTask';
import { WorkflowLLMTask } from './workflow/WorkflowLLMTask';
import { WorkflowRedirectTask } from './workflow/WorkflowRedirectTask';
import { WorkflowRootfiSyncTask } from './workflow/WorkflowRootfiSyncTask';
import { WorkflowSystemTask } from './workflow/WorkflowSystemTask';

export const ServiceRequestTaskRenderer = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { serviceRequest, workflowExecution } = context;

  const stepDefinition = useMemo(() => {
    if (!workflowExecution?.WorkflowTemplateVersion?.definition) return null;
    if (!task.workflowDefinitionStepId) return null;
    return workflowExecution.WorkflowTemplateVersion.definition.steps.find(
      (step) => step.id === task.workflowDefinitionStepId
    );
  }, [workflowExecution, task.workflowDefinitionStepId]);

  const taskComponentMap = useMemo(
    () => getServiceRequestComponentMap(serviceRequest),
    [serviceRequest]
  );

  const renderTask = () => {
    // If the task has a legacyServiceRequestTaskKey, render the legacy task component
    if (stepDefinition?.legacyServiceRequestTaskKey && taskComponentMap) {
      const LegacyTaskComponent =
        taskComponentMap[stepDefinition.legacyServiceRequestTaskKey];

      if (LegacyTaskComponent) {
        return (
          <LegacyTaskComponent
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
          />
        );
      }
    }

    // If stepDefinition is present and there is no legacyServiceRequestTaskKey, render the appropriate task component
    if (stepDefinition) {
      if (stepDefinition.type === WorkflowStepType.HUMAN_FORM) {
        return (
          <WorkflowHumanFormTask
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
          />
        );
      }

      if (stepDefinition.type === WorkflowStepType.HUMAN_CONFIRM) {
        return (
          <WorkflowHumanConfirmationTask
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
          />
        );
      }

      if (stepDefinition.type === WorkflowStepType.HUMAN_REDIRECT) {
        return (
          <WorkflowRedirectTask
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
          />
        );
      }

      if (stepDefinition.type === WorkflowStepType.HUMAN_REVIEW) {
        return (
          <WorkflowHumanReviewTask
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
          />
        );
      }

      if (stepDefinition.type === WorkflowStepType.HUMAN_ESIGN) {
        return (
          <WorkflowEsignTask
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
          />
        );
      }

      if (stepDefinition.type === WorkflowStepType.DEMO_AI_FILLING_AGENT) {
        return (
          <DemoAIFillingAgentTask
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
          />
        );
      }

      if (stepDefinition.type === WorkflowStepType.SYS_LLM) {
        return (
          <WorkflowLLMTask
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
          />
        );
      }

      if (stepDefinition.type === WorkflowStepType.SYS_ROOTFI_SYNC) {
        return (
          <WorkflowRootfiSyncTask
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
          />
        );
      }

      if (ServiceAuditorStepHelper.isServiceAuditorStep(stepDefinition)) {
        return (
          <WorkflowServiceAuditTask
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
          />
        );
      }

      const watchedAuditorStepId =
        ServiceAuditorStepHelper.findWatchedAuditorStepId(
          workflowExecution?.WorkflowTemplateVersion?.definition,
          stepDefinition
        );
      if (watchedAuditorStepId) {
        return (
          <WorkflowServiceAuditWaitTask
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
            auditorStepId={watchedAuditorStepId}
          />
        );
      }

      if (stepDefinition.type.startsWith('sys.')) {
        return (
          <WorkflowSystemTask
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
          />
        );
      }

      return (
        <WorkflowDefaultTask
          task={task}
          context={context}
          isTimelineView={isTimelineView}
          displayNumber={displayNumber}
        />
      );
    }

    // Fallback: Legacy component map logic (for non-workflow service requests)
    if (taskComponentMap) {
      const LegacyTaskComponent =
        taskComponentMap[task.legacyServiceRequestTaskTaskId];

      if (LegacyTaskComponent) {
        return (
          <LegacyTaskComponent
            task={task}
            context={context}
            isTimelineView={isTimelineView}
            displayNumber={displayNumber}
          />
        );
      }
    }

    // Workflow-driven task whose step definition wasn't resolvable (workflow
    // execution still loading, or template version missing in the loaded
    // payload), OR a placeholder UNKNOWN_TASK from workflow-sync.helper that
    // hasn't matched any specific dispatch above. Render the default
    // workflow task — it picks up the runtime state once available.
    if (
      task.workflowDefinitionStepId ||
      task.legacyServiceRequestTaskTaskId.startsWith(
        SericeRequestUnknownTask.taskId
      )
    ) {
      return (
        <WorkflowDefaultTask
          task={task}
          context={context}
          isTimelineView={isTimelineView}
          displayNumber={displayNumber}
        />
      );
    }

    // Legacy task whose service type has no component map (ODI, and every
    // other catalog entry left null). Render the generic card so the task is
    // at least visible instead of the row collapsing to an empty bullet.
    return (
      <MarkAsCompleteTask
        task={task}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
    );
  };

  const component = renderTask();
  if (!component) return null;

  return component;
};
